import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { TOOL_CALL_TOOLTIPS } from './tool-call-copy';

/**
 * KIR-4836 — presentation logic for the `tool-call-scan` alert panel.
 *
 * Pure functions (no JSX), mirroring `alert-detail-cards.helpers.ts`, so the
 * scoring/ordering/threshold rules can be unit-tested without rendering.
 */

export type ToolCallScanEvidence = NonNullable<GroupedAlert['toolCallScan']>;

/**
 * Scores are model probabilities in 0–1. The ticket asks for 2 decimals
 * everywhere (the legacy Detection Logic renderer printed 3).
 */
export function formatToolCallScore(value: number): string {
  return value.toFixed(2);
}

/** Decimals that render `threshold` without losing it, clamped to 2-3. */
function thresholdDecimals(threshold: number): number {
  return Number(threshold.toFixed(2)) === threshold ? 2 : 3;
}

/**
 * The threshold at its own precision rather than a flat 2 decimals.
 *
 * The live cutoff is `TOOL_CALL_ANALYSIS_MAL_SCORE_THRESHOLD = 0.496`, which
 * `toFixed(2)` prints as `0.50` -- so the card claimed a cutoff the backend
 * never used, and every flagged score in `[0.496, 0.505)` collapsed onto the
 * same `0.50 / 0.50` reading that this bar exists to pull apart (PR #2685
 * review). A threshold already exact at 2 decimals still prints as 2.
 */
export function formatToolCallThreshold(threshold: number): string {
  return threshold.toFixed(thresholdDecimals(threshold));
}

/**
 * Precision ceiling for the escalation below. Model scores are float32
 * probabilities; six decimals is already past anything the pipeline produces.
 */
const MAX_SCORE_DECIMALS = 6;

/**
 * A score printed so that it reads on the same side of the printed threshold
 * as the backend put it on.
 *
 * The ticket asks for 2 decimals, but 2 decimals can misstate the comparison:
 * `0.4955` against the live `0.496` cutoff prints as `0.50`, which reads as
 * flagged on a call that was allowed. Borrowing the threshold's own precision
 * is not enough either -- `0.4959` then prints as exactly `0.496`, which reads
 * as at-threshold, and at-threshold is flagged (PR #2685 review). So the
 * precision escalates until the two printed numbers agree with the real
 * verdict, and stops at the first one that does.
 *
 * A flagged score always settles inside the loop: rounding is monotonic, so
 * `round(score) >= round(threshold)` holds at whatever precision prints the
 * threshold exactly. Only an allowed score can exhaust the loop, by sitting
 * within half a unit of the printed cutoff -- and there truncating, which can
 * only move a number away from a cutoff above it, is what rounding cannot do.
 */
export function formatToolCallScoreAgainstThreshold(
  score: number,
  threshold: number
): string {
  const printedThreshold = Number(formatToolCallThreshold(threshold));
  const isFlagged = score >= threshold;
  for (let decimals = 2; decimals <= MAX_SCORE_DECIMALS; decimals += 1) {
    const text = score.toFixed(decimals);
    if (Number(text) >= printedThreshold === isFlagged) return text;
  }
  const scale = 10 ** MAX_SCORE_DECIMALS;
  return (Math.floor(score * scale) / scale).toFixed(MAX_SCORE_DECIMALS);
}

/**
 * Turn a raw model label into display text: `destructive_command` →
 * `Destructive command`.
 *
 * The label taxonomy is deliberately NOT enumerated in the contract (it is baked
 * into the model-machine deployment — see `contracts/v2/tool-call-scan.ts`), so
 * there is no mapping table to look a label up in: whatever the model returns
 * has to render. Sentence case (not Title Case) matches the mockup. An
 * all-caps token is left alone so an acronym does not come back as `Aws`.
 */
export function humanizeSignalLabel(key: string): string {
  const words = key
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .split(/[\s_-]+/)
    .filter(word => word.length > 0)
    .map(word => (word === word.toUpperCase() ? word : word.toLowerCase()));
  if (words.length === 0) return key;
  const [first, ...rest] = words as [string, ...string[]];
  const head =
    first === first.toUpperCase()
      ? first
      : first.charAt(0).toUpperCase() + first.slice(1);
  return [head, ...rest].join(' ');
}

/** Bar colour band for one category signal, relative to the policy threshold. */
export type SignalTone = 'high' | 'medium' | 'low';

/**
 * The fraction of the threshold at which a category stops reading as noise and
 * starts reading as "close to flagging". Chosen so the mockup's numbers land in
 * the bands it draws: at threshold 0.50, 0.41 is amber and 0.27 is gray.
 */
export const SIGNAL_MEDIUM_BAND = 0.6;

/**
 * Threshold-relative bands, so the colours survive a re-tuned cutoff. That
 * cutoff is a fixed backend constant, not a policy sensitivity setting (PR
 * #2685 review), but re-calibrating it is a backend release -- so the bands are
 * expressed against whatever threshold the row carries rather than a literal.
 * With no threshold there is nothing to be relative to, so every bar stays
 * neutral rather than inventing a scale.
 *
 * These bands only shade the bars. The verdict comes from the malicious score
 * alone; a category score reaching the threshold does not flag anything.
 */
export function signalTone(score: number, threshold?: number): SignalTone {
  if (threshold === undefined || threshold <= 0) return 'low';
  if (score >= threshold) return 'high';
  if (score >= threshold * SIGNAL_MEDIUM_BAND) return 'medium';
  return 'low';
}

/** Tailwind fill colour per band. */
export const SIGNAL_BAR_CLASSES: Record<SignalTone, string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-slate-400 dark:bg-slate-500',
};

export interface CategorySignalRow {
  /** Raw model label, used as the React key. */
  key: string;
  /** Display text. */
  label: string;
  score: number;
  /**
   * `score` printed against the same threshold that shaded the bar, so the top
   * category never disagrees with the malicious score it produced.
   */
  scoreText: string;
  /** Bar width as a percentage of a full 0–1 scale. */
  percent: number;
  tone: SignalTone;
  /** True for the label that produced the malicious score on a flagged call. */
  flagged: boolean;
}

/**
 * Every model label as a bar, highest first — the same ordering (score desc,
 * then label asc so equal scores are stable) the Detection Logic renderer
 * already used for its `Class scores` rows.
 */
export function categorySignalRows(
  evidence: ToolCallScanEvidence | undefined
): CategorySignalRow[] {
  const labels = evidence?.labels;
  if (labels == null) return [];
  const threshold = evidence?.threshold;
  const topThreatLabel = evidence?.topThreatLabel;
  const isFlagged = deriveToolCallVerdict(evidence) === 'flagged';
  return Object.entries(labels)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key, score]) => ({
      key,
      label: humanizeSignalLabel(key),
      score,
      scoreText:
        threshold === undefined
          ? formatToolCallScore(score)
          : formatToolCallScoreAgainstThreshold(score, threshold),
      percent: Math.max(0, Math.min(100, score * 100)),
      tone: signalTone(score, threshold),
      flagged: isFlagged && key === topThreatLabel,
    }));
}

/**
 * `flagged` / `allowed`, or `null` when it cannot be known.
 *
 * There is no verdict field on the alert: it exists only on the transient v2
 * agent response and is never persisted (`contracts/v2/tool-call-scan.ts`). It
 * is re-derived here from the same comparison the backend made. With either
 * side of that comparison missing, the answer is `null` — the UI then shows no
 * verdict rather than guessing one.
 */
export function deriveToolCallVerdict(
  evidence: ToolCallScanEvidence | undefined
): 'flagged' | 'allowed' | null {
  const { malScore, threshold } = evidence ?? {};
  if (malScore === undefined || threshold === undefined) return null;
  return malScore >= threshold ? 'flagged' : 'allowed';
}

export interface AdditionalSignalChip {
  key: string;
  label: string;
  value: string;
  /** Render red rather than gray. */
  danger: boolean;
  tooltip: string;
}

/**
 * The session-context chips. Only signals the agent actually reported are
 * listed — an older row that predates one of them must not render as "No",
 * which would assert something the scan never measured.
 *
 * `Calls since prompt` is a count, not a yes/no, and no threshold for "too
 * many" is defined anywhere, so it never turns red.
 */
export function additionalSignalChips(
  evidence: ToolCallScanEvidence | undefined
): AdditionalSignalChip[] {
  if (evidence == null) return [];
  const chips: AdditionalSignalChip[] = [];
  const booleanSignals = [
    {
      key: 'sensitivePath',
      label: 'Sensitive path',
      value: evidence.sensitivePath,
      tooltip: TOOL_CALL_TOOLTIPS.sensitivePath,
    },
    {
      key: 'egressPrior',
      label: 'Prior egress',
      value: evidence.egressPrior,
      tooltip: TOOL_CALL_TOOLTIPS.egressPrior,
    },
    {
      key: 'unknownPkg',
      label: 'Unknown package',
      value: evidence.unknownPkg,
      tooltip: TOOL_CALL_TOOLTIPS.unknownPkg,
    },
  ] as const;
  for (const signal of booleanSignals) {
    if (signal.value === undefined) continue;
    chips.push({
      key: signal.key,
      label: signal.label,
      value: signal.value ? 'Yes' : 'No',
      danger: signal.value,
      tooltip: signal.tooltip,
    });
  }
  if (evidence.sincePrompt !== undefined) {
    chips.push({
      key: 'sincePrompt',
      label: 'Calls since prompt',
      value: String(evidence.sincePrompt),
      danger: false,
      tooltip: TOOL_CALL_TOOLTIPS.sincePrompt,
    });
  }
  return chips;
}

/**
 * The tool that was called, e.g. `Bash`.
 *
 * `toolCallScan` carries no tool name; the agent instead writes the tool name
 * into the finding's `location` for this type (`pkg/hooks/processor.go`), which
 * is why the ticket moves it into the facts strip and drops the Location box
 * that held nothing else. `undefined` when the location was anonymized away.
 */
export function toolCallToolName(alert: {
  location?: string | null;
}): string | undefined {
  const trimmed = alert.location?.trim();
  return trimmed != null && trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Whether the "Why Kirin AI flagged this" card has anything to say. A row
 * written before this evidence shipped (no backfill — the scan payload is not
 * retained) carries none of it, and an empty card is worse than no card.
 */
export function hasToolCallVerdictContent(
  evidence: ToolCallScanEvidence | undefined,
  description?: string | null
): boolean {
  return (
    Boolean(description?.trim()) ||
    evidence?.malScore !== undefined ||
    evidence?.topThreatLabel !== undefined ||
    categorySignalRows(evidence).length > 0 ||
    additionalSignalChips(evidence).length > 0
  );
}
